import { ParsedUrlQuery } from 'querystring';

import { GetStaticPaths, GetStaticProps } from 'next';

import { ImageCard } from '@common/ImageCard';
import { SignPost } from '@common/SignPost';
import { RealPeopleBanner } from '@components/RealPeopleBanner';
import { ReferencesRibbon } from '@common/ReferencesRibbon';
import { Footer } from '@components/Footer/Footer';
import { LatestStoryHero } from '@components/LatestStoryHero';
import Meta from '@components/Meta';

import {
  combinePostsForCountryVariants,
  getRecentArticles,
  postGuidesQuery,
} from '@nextUtils/guidesUtils';

import {
  CategoryPageInfoType,
  RecentArticleEdgeType,
  CategoryFieldsFragment,
} from '@nextTypes/guides';
import { ErrorTracking } from '@common/errorTracking';

type Props = {
  afterCursor: string | null;
  recentPosts: {
    edges: RecentArticleEdgeType[];
    pageInfo: CategoryPageInfoType;
  };
};

const POSTS_PER_PAGE = 9;

interface Params extends ParsedUrlQuery {
  after: string | string[] | undefined;
}

export const getStaticPaths: GetStaticPaths<Params> = () => ({
  // This will render the root-most route at build time. Anything else will be generated at run time
  paths: [{ params: { after: undefined } }],
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<Props> = async ({
  params: { after } = {},
  locale,
}) => {
  let afterCursor: string | null = null;
  // The after cursor is the second element in the `after` array
  if (Array.isArray(after) && after.length === 2) {
    afterCursor = after[1];
  }

  // There can be a maximum of 2 country variants per post - one for all countries and one for this locale -
  // so request double the number of posts before combining them
  const paginationArgs = `first: ${POSTS_PER_PAGE * 2},
    ${afterCursor ? `after: "${afterCursor}",` : ''}`;

  try {
    const query = `
      ${CategoryFieldsFragment}
      query {
        recentPosts: posts(${paginationArgs}
          where: {
          categoryName: "Stories"
          orderby:{
            field:DATE
            order:DESC
          }
          tagSlugIn: [ "all_countries", "${locale}" ]
        }) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            cursor
            node {
              title
              slug
              seo {
                metaDesc
              }
              author {
                name
                avatar {
                  url
                }
              }
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              featuredImage {
                sourceUrl
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
      }
    `;

    const json = await postGuidesQuery(query);

    return {
      props: {
        afterCursor,
        recentPosts: combinePostsForCountryVariants(
          json.data.recentPosts,
          POSTS_PER_PAGE,
        ),
      },
      // Refresh this page at most every 300 seconds
      // If a request comes in within 300 seconds, it will serve the existing cached page
      // If a request comes in outside of 300 seconds, it will serve the cached page - but re-fetch the page in the background
      revalidate: 300,
    };
  } catch (err) {
    ErrorTracking.track(err);
    // We do not handle this error, but it is handled by NextJS https://github.com/vercel/next.js/discussions/11180#discussioncomment-2399
    throw err;
  }
};

const Stories = ({ afterCursor, recentPosts }: Props): JSX.Element => {
  const title = 'Second Nature Stories';

  const metaDescription =
    'Learn about the range of achievements and challenges that individuals experienced while completing the Second Nature programme.';

  const recentArticles = getRecentArticles(recentPosts);

  const heroArticle = recentArticles[0];

  const recentArticleTiles = recentArticles
    .slice(1)
    .map(article => (
      <ImageCard
        title={article.title}
        subtitle={article.description}
        image={article.featuredImageUrl}
        altText={article.title}
        key={article.title}
        href={article.href}
      />
    ));

  const relativeCanonicalURL = `/stories${
    afterCursor ? `/after/${afterCursor}` : ''
  }`;

  return (
    <>
      <Meta
        title={title}
        metaDescription={metaDescription}
        relativeCanonicalURL={relativeCanonicalURL}
      />
      <LatestStoryHero
        title={heroArticle.title}
        description={heroArticle.description}
        href={heroArticle.href}
        image={heroArticle.heroImageUrl}
      />
      <div className="flex items-center bg-blue py-16 lg:py-[103px]">
        <div className="m-auto grid grid-cols-1 justify-items-center gap-6 lg:grid-cols-2">
          {recentArticleTiles}
        </div>
      </div>
      <RealPeopleBanner />
      <SignPost version="A" />
      <ReferencesRibbon />
      <Footer />
    </>
  );
};

export default Stories;
